import logger from "@shared/utils/logger";

export type DashboardNavigationTrace = {
  label: string;
  expectedCount: number | string;
  countSource: string;
  targetUrl: string;
  metricKey?: string;
};

const TRACE_STORAGE_KEY = "nervedx:dashboard-trace";
const LAST_NAV_STORAGE_KEY = "nervedx:dashboard-last-nav";

export function isDashboardNavigationTraceEnabled(): boolean {
  if (import.meta.env.DEV) {
    return true;
  }

  try {
    return (
      import.meta.env.VITE_DASHBOARD_DEBUG === "true" ||
      window.localStorage.getItem(TRACE_STORAGE_KEY) === "1"
    );
  } catch {
    return import.meta.env.VITE_DASHBOARD_DEBUG === "true";
  }
}

export function recordDashboardNavigation(
  trace: DashboardNavigationTrace,
): void {
  if (!isDashboardNavigationTraceEnabled()) {
    return;
  }

  const payload = {
    ...trace,
    recordedAt: new Date().toISOString(),
  };

  logger.info("[dashboard-nav] click", payload);
  console.groupCollapsed(
    `[dashboard-nav] ${trace.label} → ${trace.expectedCount} @ ${trace.targetUrl}`,
  );
  console.info("countSource:", trace.countSource);
  console.info("metricKey:", trace.metricKey || "(none)");
  console.trace("[dashboard-nav] navigation stack");
  console.groupEnd();

  try {
    window.sessionStorage.setItem(
      LAST_NAV_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // ignore storage failures in private browsing
  }
}

export function readLastDashboardNavigationTrace():
  | DashboardNavigationTrace
  | null {
  try {
    const raw = window.sessionStorage.getItem(LAST_NAV_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DashboardNavigationTrace;
  } catch {
    return null;
  }
}

export function logDestinationCountVerification(input: {
  page: string;
  filters: Record<string, string | null | undefined>;
  expected?: DashboardNavigationTrace | null;
  actualCount: number;
  totalOverall?: number;
}): void {
  if (!isDashboardNavigationTraceEnabled()) {
    return;
  }

  const expectedCount = input.expected?.expectedCount;
  if (!input.expected || expectedCount == null || expectedCount === "") {
    logger.debug("[dashboard-nav] destination count skipped", {
      page: input.page,
      filters: input.filters,
      actualCount: input.actualCount,
      totalOverall: input.totalOverall ?? null,
      reason: "missing dashboard trace",
    });
    return;
  }

  const numericExpected =
    typeof expectedCount === "number"
      ? expectedCount
      : Number.parseInt(String(expectedCount || ""), 10);
  const countsMatch =
    Number.isFinite(numericExpected) && numericExpected === input.actualCount;

  const payload = {
    page: input.page,
    filters: input.filters,
    expectedLabel: input.expected?.label || null,
    expectedCount: expectedCount ?? null,
    actualCount: input.actualCount,
    totalOverall: input.totalOverall ?? null,
    countsMatch,
    targetUrl: input.expected?.targetUrl || null,
    countSource: input.expected?.countSource || null,
  };

  if (countsMatch) {
    logger.info("[dashboard-nav] destination count verified", payload);
  } else {
    logger.warn("[dashboard-nav] destination count mismatch", payload);
    console.warn("[dashboard-nav] COUNT MISMATCH", payload);
    console.trace("[dashboard-nav] mismatch stack");
  }
}
